import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationInfo } from '@/types/event';

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ pagination, onPageChange }) => {
  const { current_page, last_page, links } = pagination;

  if (last_page <= 1) return null;

  const handlePageClick = (page: number | null) => {
    if (page && page !== current_page) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];
    const maxVisiblePages = window.innerWidth < 640 ? 3 : 5;
    
    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageClick(current_page - 1)}
        disabled={current_page === 1}
        className={`px-2 sm:px-3 py-1 rounded-full transition-all bg-white/5 border shadow-lg overflow-hidden hover:shadow-orange-700/30 duration-300 ease-in-out hover:scale-105 flex items-center justify-center min-w-8 sm:min-w-10 h-8 sm:h-10
          ${current_page === 1
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-gray-300 hover:text-white hover:bg-white/20'
        }`}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} className="sm:w-4 sm:h-4" />
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageClick(1)}
          className="px-2 sm:px-3 py-1 bg-white/5 rounded-full text-gray-300 hover:text-white hover:bg-white/20 min-w-8 sm:min-w-10 h-8 sm:h-10 flex items-center justify-center"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="px-1 sm:px-2 text-gray-500 flex items-center">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={`px-2 sm:px-3 py-1 rounded-full transition-all min-w-8 sm:min-w-10 h-8 sm:h-10 flex items-center justify-center ${
            i === current_page
              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
              : 'bg-white/5 text-gray-300 hover:text-white hover:bg-white/20'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < last_page) {
      if (endPage < last_page - 1) {
        pages.push(
          <span key="ellipsis2" className="px-1 sm:px-2 text-gray-500 flex items-center">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={last_page}
          onClick={() => handlePageClick(last_page)}
          className="px-2 sm:px-3 py-1 bg-white/5 rounded-full text-gray-300 hover:text-white hover:bg-white/20 min-w-8 sm:min-w-10 h-8 sm:h-10 flex items-center justify-center"
        >
          {last_page}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageClick(current_page + 1)}
        disabled={current_page === last_page}
        className={`px-2 sm:px-3 py-1 rounded-full transition-all min-w-8 sm:min-w-10 h-8 sm:h-10 flex items-center justify-center ${
          current_page === last_page
            ? 'bg-white/5 text-gray-500 cursor-not-allowed'
            : 'bg-white/5 text-gray-300 hover:text-white hover:bg-white/20'
        }`}
        aria-label="Next page"
      >
        <ChevronRight size={14} className="sm:w-4 sm:h-4" />
      </button>
    );

    return pages;
  };

  return (
    <div className="flex justify-center mt-6 sm:mt-8 md:mt-10 space-x-1 sm:space-x-2 overflow-x-auto px-2 sm:px-0 py-2">
      {renderPageNumbers()}
    </div>
  );
};

export default Pagination;